'use client';

import { useRef, useState } from 'react';

import Image from 'next/image';

import {
  ArrowRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Gift,
  Heart,
  Lock,
  Tag,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogTitle } from '@kit/ui/dialog';
import { cn } from '@kit/ui/utils';

import { CheckoutOverlay } from '~/components/billing/checkout-overlay';
import { useDisplayCurrency } from '~/components/currency-provider';
import { trackFunnelEvent } from '~/lib/analytics/track';
import { type DisplayCurrency, formatPrice } from '~/lib/currency';

import { RoseIcon } from './rose-icon';
import { scriptFont } from './script-font';
import {
  formatCountdown,
  useFirstPurchaseOffer,
} from './use-first-purchase-offer';

// Rose packs matching billing.config.ts
const ROSE_PACKS = [
  { id: 'coins-700', roses: 700, price: 4.99, tag: 'starter' },
  { id: 'coins-1600', roses: 1600, price: 9.99, tag: 'popular' },
  { id: 'coins-3500', roses: 3500, price: 19.99, tag: 'bestValue' },
  { id: 'coins-8000', roses: 8000, price: 39.99, tag: 'pro' },
  { id: 'coins-18000', roses: 18000, price: 79.99, tag: 'vip' },
] as const;

type RosePack = (typeof ROSE_PACKS)[number];

const DEFAULT_PACK = 'coins-1600';
const PINK = '#ec4899';
const CARD_GAP = 14;

const NEUTRAL_TAG: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.18)',
  color: 'rgba(255,255,255,0.85)',
};

const TAG_STYLE: Record<RosePack['tag'], React.CSSProperties> = {
  starter: NEUTRAL_TAG,
  popular: {
    background: 'rgba(236,72,153,0.22)',
    border: '1px solid rgba(244,114,182,0.6)',
    color: '#fbcfe8',
  },
  bestValue: {
    background: 'rgba(168,85,247,0.22)',
    border: '1px solid rgba(192,132,252,0.55)',
    color: '#e9d5ff',
  },
  pro: NEUTRAL_TAG,
  vip: {
    background: 'rgba(245,197,66,0.16)',
    border: '1px solid rgba(245,197,66,0.55)',
    color: '#fde68a',
  },
};

/**
 * Card widths per breakpoint (3 on desktop, 2 on tablets, ~2 on phones).
 * A scoped stylesheet rather than arbitrary Tailwind classes, which the dev
 * build sometimes drops.
 */
const TRACK_CSS = `
.rose-track { scrollbar-width: none; }
.rose-track::-webkit-scrollbar { display: none; }
.rose-pack { flex: 0 0 46%; }
@media (min-width: 640px) { .rose-pack { flex-basis: calc((100% - ${CARD_GAP}px) / 2); } }
@media (min-width: 1024px) { .rose-pack { flex-basis: calc((100% - ${CARD_GAP * 2}px) / 3); } }
`;

/**
 * Display-only mirror of applyPromoDiscount in
 * lib/server/billing/promo-codes.ts (incl. the €0.50 gateway floor) — the
 * checkout route recomputes the real charge server-side.
 */
function promoDiscountedPrice(price: number, percentOff: number): number {
  const discounted = Math.round(price * (1 - percentOff / 100) * 100) / 100;
  return Math.max(0.5, discounted);
}

interface PromoState {
  input: string;
  applied: { code: string; percentOff: number } | null;
  checking: boolean;
  error: string | null;
  open: boolean;
}

interface BuyCoinsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPurchaseComplete?: () => void;
}

export function BuyCoinsModal({
  open,
  onOpenChange,
  onPurchaseComplete,
}: BuyCoinsModalProps) {
  const [loadingPack, setLoadingPack] = useState<string | null>(null);
  const [checkoutToken, setCheckoutToken] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>(DEFAULT_PACK);
  const [activeIndex, setActiveIndex] = useState(0);
  // Scroll positions the track can actually reach (packs minus the ones
  // already in view, plus one). Re-measured whenever the track resizes.
  const [stops, setStops] = useState<number>(ROSE_PACKS.length);
  const [promo, setPromo] = useState<PromoState>({
    input: '',
    applied: null,
    checking: false,
    error: null,
    open: false,
  });
  const trackRef = useRef<HTMLDivElement | null>(null);
  const trackedOpen = useRef(false);
  const offer = useFirstPurchaseOffer(open);
  const { currency } = useDisplayCurrency();
  const { t } = useTranslation('billing');

  // Funnel tracking: once per open, without an effect.
  if (open && !trackedOpen.current) {
    trackedOpen.current = true;
    trackFunnelEvent('buy_coins_opened');
  } else if (!open && trackedOpen.current) {
    trackedOpen.current = false;
  }

  const priceOf = (pack: RosePack) =>
    promo.applied
      ? promoDiscountedPrice(pack.price, promo.applied.percentOff)
      : pack.price;
  const selected = ROSE_PACKS.find((p) => p.id === selectedId) ?? ROSE_PACKS[1];

  // Validated via /api/billing/promo when applied; the checkout route
  // re-validates on purchase (server is the authority).
  const applyPromo = async () => {
    const code = promo.input.trim();
    if (!code) return;

    setPromo((p) => ({ ...p, checking: true, error: null }));

    try {
      const response = await fetch('/api/billing/promo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, planId: ROSE_PACKS[0].id }),
      });
      const data = await response.json();

      if (data.valid) {
        setPromo((p) => ({
          ...p,
          input: '',
          applied: { code: data.code, percentOff: data.percentOff },
          checking: false,
          error: null,
        }));
      } else {
        setPromo((p) => ({
          ...p,
          checking: false,
          error: t(`coinModal.promo.reasons.${data.reason ?? 'invalid'}`),
        }));
      }
    } catch {
      setPromo((p) => ({
        ...p,
        checking: false,
        error: t('coinModal.promo.reasons.invalid'),
      }));
    }
  };

  const handlePurchase = async (packId: string) => {
    setLoadingPack(packId);
    trackFunnelEvent('checkout_started', { packId });

    try {
      const response = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId: packId,
          ...(promo.applied ? { promoCode: promo.applied.code } : {}),
        }),
      });
      const data = await response.json();

      if (!response.ok || !data.checkoutToken) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      setCheckoutToken(data.checkoutToken);
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setLoadingPack(null);
    }
  };

  const cardStep = () => {
    const first = trackRef.current?.firstElementChild as HTMLElement | null;
    return first ? first.offsetWidth + CARD_GAP : 0;
  };

  const measureStops = (el: HTMLDivElement) => {
    const step = cardStep();
    if (step) {
      setStops(Math.round((el.scrollWidth - el.clientWidth) / step) + 1);
    }
  };

  const attachTrack = (el: HTMLDivElement | null) => {
    trackRef.current = el;
    if (!el) return;
    const observer = new ResizeObserver(() => measureStops(el));
    observer.observe(el);
    return () => observer.disconnect();
  };

  const scrollToIndex = (index: number) => {
    trackRef.current?.scrollTo({
      left: index * cardStep(),
      behavior: 'smooth',
    });
  };

  // When the user clicks Buy, render the shared CheckoutOverlay (same
  // component as /pricing). Back returns to the packs.
  if (checkoutToken) {
    return (
      <CheckoutOverlay
        checkoutToken={checkoutToken}
        onBack={() => setCheckoutToken(null)}
        onComplete={() => {
          setCheckoutToken(null);
          onPurchaseComplete?.();
        }}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-[95vw] max-w-[95vw] overflow-visible border-0 bg-transparent p-0 shadow-none sm:max-w-2xl lg:max-w-[1040px] [&>button]:hidden"
      >
        <style>{TRACK_CSS}</style>
        <div
          className="relative grid max-h-[92vh] overflow-x-hidden overflow-y-auto lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]"
          style={{
            borderRadius: 24,
            border: '1px solid rgba(244,114,182,0.35)',
            background:
              'radial-gradient(120% 90% at 100% 0%, rgba(236,72,153,0.14) 0%, rgba(11,7,16,0) 55%), #0d0810',
            boxShadow: '0 30px 80px -30px rgba(236,72,153,0.45)',
          }}
        >
          <SidePhoto />

          <div className="relative min-w-0 px-4 pt-5 pb-5 sm:px-8 sm:pt-7 lg:px-9">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label={t('coinModal.close')}
              className="absolute top-4 right-4 flex items-center justify-center rounded-full text-white/85 transition-colors hover:bg-white/10 hover:text-white sm:top-5 sm:right-5"
              style={{
                width: 42,
                height: 42,
                border: '1px solid rgba(255,255,255,0.18)',
                background: 'rgba(0,0,0,0.25)',
              }}
            >
              <X className="h-5 w-5" />
            </button>

            <DialogTitle
              className="pr-12 text-[34px] leading-none font-bold text-white sm:text-[46px]"
              style={{ fontFamily: 'var(--font-display), Georgia, serif' }}
            >
              {t('coinModal.titleLead')}{' '}
              <span
                style={{
                  background: 'linear-gradient(180deg,#ffb3dd,#ec4899)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                {t('coinModal.titleHighlight')}
              </span>
            </DialogTitle>
            <p className="mt-2 text-sm text-white/75 sm:text-lg">
              {t('coinModal.choosePackage')}
            </p>

            {offer?.remainingMs != null && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-pink-400/30 bg-pink-500/10 px-3 py-1.5">
                <Gift
                  className="h-4 w-4 shrink-0"
                  style={{ color: '#f472b6' }}
                />
                <span className="text-xs font-medium text-pink-100/90">
                  {t('coinModal.outOfCoins.firstPurchase.line')}
                </span>
                <span className="font-mono text-xs font-bold text-pink-300 tabular-nums">
                  {formatCountdown(offer.remainingMs)}
                </span>
              </div>
            )}

            {/* Pack carousel */}
            <div className="relative mt-5">
              <CarouselArrow
                side="left"
                label={t('coinModal.previous')}
                disabled={activeIndex === 0}
                onClick={() => scrollToIndex(Math.max(0, activeIndex - 1))}
              />
              <div
                ref={attachTrack}
                onScroll={(e) => {
                  const step = cardStep();
                  if (step) {
                    setActiveIndex(
                      Math.round(e.currentTarget.scrollLeft / step),
                    );
                  }
                }}
                className="rose-track flex snap-x snap-mandatory overflow-x-auto py-1"
                style={{ gap: CARD_GAP }}
              >
                {ROSE_PACKS.map((pack) => (
                  <PackCard
                    key={pack.id}
                    pack={pack}
                    selected={pack.id === selectedId}
                    price={priceOf(pack)}
                    originalPrice={promo.applied ? pack.price : null}
                    currency={currency}
                    isLoading={loadingPack === pack.id}
                    disabled={Boolean(loadingPack)}
                    onSelect={() => setSelectedId(pack.id)}
                    onPurchase={() => void handlePurchase(pack.id)}
                  />
                ))}
              </div>
              <CarouselArrow
                side="right"
                label={t('coinModal.next')}
                disabled={activeIndex >= stops - 1}
                onClick={() =>
                  scrollToIndex(Math.min(stops - 1, activeIndex + 1))
                }
              />
            </div>

            <div className="mt-4 flex justify-center gap-2">
              {Array.from({ length: stops }, (_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`${i + 1} / ${stops}`}
                  onClick={() => scrollToIndex(i)}
                  className="rounded-full transition-all"
                  style={{
                    width: 8,
                    height: 8,
                    background:
                      i === activeIndex ? PINK : 'rgba(255,255,255,0.25)',
                    boxShadow:
                      i === activeIndex
                        ? '0 0 8px rgba(236,72,153,0.8)'
                        : 'none',
                  }}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={() => void handlePurchase(selected.id)}
              disabled={Boolean(loadingPack)}
              className="mt-5 flex w-full items-center justify-center gap-3 rounded-2xl text-base font-semibold text-white transition-[filter] hover:brightness-110 disabled:opacity-70 sm:text-lg"
              style={{
                height: 56,
                background: 'linear-gradient(180deg,#f472b6 0%,#db2777 100%)',
                boxShadow:
                  '0 10px 30px -10px rgba(236,72,153,0.9), inset 0 1px 0 rgba(255,255,255,0.25)',
              }}
              data-test="roses-modal-continue"
            >
              {loadingPack === selected.id
                ? t('coinModal.processing')
                : `${t('coinModal.continue')} — ${formatPrice(
                    priceOf(selected).toFixed(2),
                    currency,
                  )}`}
              <ArrowRight className="h-5 w-5" />
            </button>

            <Footer promo={promo} setPromo={setPromo} applyPromo={applyPromo} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SidePhoto() {
  const { t } = useTranslation('billing');
  return (
    <div className="relative hidden overflow-hidden lg:block">
      <Image
        src="/images/banners/roses-modal-room.webp"
        alt=""
        fill
        sizes="380px"
        className="object-cover"
        style={{ objectPosition: '45% 20%' }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(13,8,16,0) 55%, rgba(13,8,16,0.55) 100%), linear-gradient(90deg, rgba(13,8,16,0) 78%, #0d0810 100%)',
        }}
      />
      <p
        className={cn(
          scriptFont.className,
          'absolute bottom-6 left-6 leading-[0.95]',
        )}
        style={{
          fontSize: 44,
          color: '#f9a8d4',
          transform: 'rotate(-8deg)',
          textShadow: '0 2px 18px rgba(236,72,153,0.55)',
        }}
      >
        {t('coinModal.side.line1')}
        <br />
        <span style={{ paddingLeft: 18 }}>{t('coinModal.side.line2')}</span>
        <Heart
          className="mt-1 ml-16 block h-6 w-6"
          strokeWidth={1.4}
          aria-hidden
        />
      </p>
    </div>
  );
}

function CarouselArrow({
  side,
  label,
  disabled,
  onClick,
}: {
  side: 'left' | 'right';
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="absolute top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center rounded-full text-white transition-opacity hover:bg-white/10 disabled:opacity-0 sm:flex"
      style={{
        [side]: -22,
        width: 44,
        height: 44,
        background: '#0d0810',
        border: '1px solid rgba(255,255,255,0.22)',
      }}
    >
      <Icon className="h-5 w-5" />
    </button>
  );
}

function PackCard({
  pack,
  selected,
  price,
  originalPrice,
  currency,
  isLoading,
  disabled,
  onSelect,
  onPurchase,
}: {
  pack: RosePack;
  selected: boolean;
  price: number;
  originalPrice: number | null;
  currency: DisplayCurrency;
  isLoading: boolean;
  disabled: boolean;
  onSelect: () => void;
  onPurchase: () => void;
}) {
  const { t } = useTranslation('billing');

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect();
      }}
      aria-pressed={selected}
      className={cn(
        'rose-pack flex cursor-pointer snap-start flex-col items-center px-3 pt-5 pb-4 text-center transition-[border-color,box-shadow,background]',
        disabled && !isLoading && 'opacity-60',
      )}
      style={{
        borderRadius: 18,
        border: selected
          ? '1.5px solid rgba(244,114,182,0.9)'
          : '1px solid rgba(255,255,255,0.14)',
        background: selected
          ? 'linear-gradient(180deg, rgba(236,72,153,0.14), rgba(255,255,255,0.02))'
          : 'rgba(255,255,255,0.025)',
        boxShadow: selected ? '0 0 28px -6px rgba(236,72,153,0.6)' : 'none',
      }}
      data-test={`rose-pack-${pack.roses}`}
    >
      <RoseIcon className="h-12 w-12 sm:h-14 sm:w-14" />
      <div className="mt-2 text-[26px] leading-none font-bold text-white tabular-nums sm:text-[30px]">
        {pack.roses.toLocaleString()}
      </div>
      <div className="mt-1 text-sm text-white/75 sm:text-base">
        {t('coinModal.goldCoins')}
      </div>
      <span
        className="mt-2.5 rounded-full px-3 py-1 text-[11px] font-medium whitespace-nowrap sm:text-xs"
        style={TAG_STYLE[pack.tag]}
      >
        {t(`coinModal.tags.${pack.tag}`)}
      </span>
      <div className="mt-3 flex flex-col items-center">
        {originalPrice !== null && (
          <span className="text-xs text-white/40 line-through">
            {formatPrice(originalPrice, currency)}
          </span>
        )}
        <span className="text-[22px] font-bold text-white sm:text-[24px]">
          {formatPrice(price.toFixed(2), currency)}
        </span>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPurchase();
        }}
        disabled={disabled}
        className="mt-3 w-full rounded-xl py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/15 disabled:cursor-not-allowed sm:text-base"
        style={{
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        {isLoading ? t('coinModal.processing') : t('coinModal.buy')}
      </button>
    </div>
  );
}

function Footer({
  promo,
  setPromo,
  applyPromo,
}: {
  promo: PromoState;
  setPromo: React.Dispatch<React.SetStateAction<PromoState>>;
  applyPromo: () => Promise<void>;
}) {
  const { t } = useTranslation('billing');

  return (
    <div className="mt-5 border-t border-white/10 pt-4">
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs text-white/70 sm:text-sm">
        <span className="inline-flex items-center gap-2">
          <Lock className="h-4 w-4" />
          {t('coinModal.secureCheckout')}
        </span>
        <span aria-hidden className="hidden h-5 w-px bg-white/15 sm:block" />
        {promo.applied ? (
          <span className="inline-flex items-center gap-2 text-emerald-300">
            <Tag className="h-4 w-4" />
            {t('coinModal.promo.applied', {
              code: promo.applied.code,
              percent: promo.applied.percentOff,
            })}
            <button
              type="button"
              onClick={() =>
                setPromo((p) => ({ ...p, applied: null, error: null }))
              }
              aria-label={t('coinModal.promo.remove')}
              className="text-emerald-300/70 hover:text-white"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setPromo((p) => ({ ...p, open: !p.open }))}
            className="inline-flex items-center gap-2 text-pink-300 underline underline-offset-4"
            aria-expanded={promo.open}
          >
            <Gift className="h-4 w-4" />
            {t('coinModal.havePromo')}
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform',
                promo.open && 'rotate-180',
              )}
            />
          </button>
        )}
      </div>

      {promo.open && !promo.applied && (
        <div className="mt-3 flex items-center justify-center gap-2">
          <input
            type="text"
            value={promo.input}
            onChange={(e) =>
              setPromo((p) => ({
                ...p,
                input: e.target.value.toUpperCase(),
                error: null,
              }))
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') void applyPromo();
            }}
            placeholder={t('coinModal.promo.placeholder')}
            className="w-40 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white uppercase placeholder:text-white/40 focus:border-pink-400/50 focus:outline-none"
            maxLength={32}
            data-test="promo-code-input"
          />
          <button
            type="button"
            onClick={() => void applyPromo()}
            disabled={promo.checking || !promo.input.trim()}
            className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/85 hover:bg-white/10 disabled:opacity-50"
          >
            {promo.checking
              ? t('coinModal.processing')
              : t('coinModal.promo.apply')}
          </button>
        </div>
      )}
      {promo.error && (
        <p className="mt-2 text-center text-[11px] text-red-400">
          {promo.error}
        </p>
      )}
    </div>
  );
}
